import type { TextChunk } from './text-chunker';
import { GatewayErrorCode, isGatewayError } from './error-codes';
import { logger } from '../logger';

export interface RunSessionChunkedOptions<TFinal> {
  /**
   * Called after each step (chunk extraction + final check).
   * NOTE: progress is NOT monotonic — it resets to step 1 if the pass
   * restarts after a session expiry.
   */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Drive the session-scoped chunk loop (#81): extract each chunk into the
 * gateway-side workspace (accumulates under the derived session +
 * document_id), then run one check over the accumulated document.
 *
 * If the gateway reports SESSION_EXPIRED mid-sequence (workspace reaped by
 * idle timeout or a backend restart), the whole document is re-extracted
 * from chunk 0 — once. A second expiry surfaces to the caller.
 */
export async function runSessionChunked<TChunk, TFinal>(
  chunks: TextChunk[],
  extractChunk: (chunk: TextChunk, index: number) => Promise<TChunk>,
  checkDocument: () => Promise<TFinal>,
  merge: (
    chunkResults: (TChunk | null)[],
    checkResult: TFinal | null,
    failedIndices: number[]
  ) => TFinal,
  options?: RunSessionChunkedOptions<TFinal>
): Promise<TFinal> {
  const maxRestarts = 1;
  for (let restart = 0; ; restart++) {
    try {
      return await runOnce(chunks, extractChunk, checkDocument, merge, options);
    } catch (error) {
      if (
        isGatewayError(error, GatewayErrorCode.SESSION_EXPIRED) &&
        restart < maxRestarts
      ) {
        logger.warn(
          '[chunk] gateway session expired mid-sequence; restarting extraction from chunk 0'
        );
        continue;
      }
      throw error;
    }
  }
}

async function runOnce<TChunk, TFinal>(
  chunks: TextChunk[],
  extractChunk: (chunk: TextChunk, index: number) => Promise<TChunk>,
  checkDocument: () => Promise<TFinal>,
  merge: (
    chunkResults: (TChunk | null)[],
    checkResult: TFinal | null,
    failedIndices: number[]
  ) => TFinal,
  options?: RunSessionChunkedOptions<TFinal>
): Promise<TFinal> {
  const totalSteps = chunks.length + 1; // chunks + final check
  const results: (TChunk | null)[] = new Array(chunks.length).fill(null);
  const failedIndices: number[] = [];

  for (let i = 0; i < chunks.length; i++) {
    try {
      results[i] = await extractChunk(chunks[i]!, i);
    } catch (error) {
      // Session expiry invalidates the whole accumulated workspace — bubble
      // up so the outer loop restarts from chunk 0.
      if (isGatewayError(error, GatewayErrorCode.SESSION_EXPIRED)) throw error;
      // A caller abort must stop the whole run, not count as a failed chunk.
      const isAbort = error instanceof Error && error.name === 'AbortError';
      if (isAbort) throw error;
      logger.warn(
        `[chunk] chunk=${i + 1}/${chunks.length} chars=${chunks[i]!.text.length} ` +
          `extraction failed: ${(error as Error).message}`
      );
      failedIndices.push(i);
    }
    options?.onProgress?.(i + 1, totalSteps);
  }

  if (failedIndices.length === chunks.length) {
    throw new Error(
      `All ${chunks.length} chunks failed. The document could not be processed.`
    );
  }

  const checkResult = await checkDocument();
  options?.onProgress?.(totalSteps, totalSteps);
  return merge(results, checkResult, failedIndices);
}
